// components/project-details/ImageGallery.js
import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import styles from './ProjectDetails.module.css';

const ImageGallery = ({ images, projectId, onImagesUpdate }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb > 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  const handleRemoveImage = (imageUrl) => {
    const confirmRemove = window.confirm('Are you sure you want to remove this image? This cannot be undone.');
    if (confirmRemove) {
      setImagesToRemove([...imagesToRemove, imageUrl]);
    }
  };

  const handleSave = async () => {
    if (imagesToRemove.length === 0) return;
    
    setIsSaving(true);
    try {
      const updatedImages = images.filter(img => !imagesToRemove.includes(img.url));
      
      await updateDoc(doc(db, 'projects', projectId), {
        productImages: updatedImages,
        updatedAt: new Date()
      });
      
      onImagesUpdate(updatedImages);
      setImagesToRemove([]);
      alert('Images updated successfully');
    } catch (error) {
      console.error('Error updating images:', error);
      alert('Failed to update images');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setImagesToRemove([]);
  };

  const visibleImages = images.filter(img => !imagesToRemove.includes(img.url));

  return (
    <>
      {imagesToRemove.length > 0 && (
        <div style={{ 
          padding: 'var(--spacing-m)', 
          background: 'var(--Amber-50)', 
          borderRadius: 'var(--radius-s)',
          marginTop: 'var(--spacing-m)',
          marginBottom: 'var(--spacing-m)'
        }}>
          <p style={{ 
            margin: 0, 
            marginBottom: 'var(--spacing-s)',
            color: 'var(--Amber-800)' 
          }}>
            {imagesToRemove.length} image(s) marked for removal
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: 'white',
                color: 'var(--Text-Primary)',
                border: '1px solid var(--Stroke-Light)',
                borderRadius: 'var(--radius-xs)',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: 'var(--Red-600)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-xs)',
                cursor: 'pointer',
                opacity: isSaving ? 0.5 : 1
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.imageGrid}>
        {visibleImages.map((image, index) => (
          <div
            key={index}
            className={styles.imageThumbnail}
            style={{ position: 'relative' }}
          >
            <img
              src={image.url}
              alt={image.name}
              className={styles.thumbnailImage}
              onClick={() => setSelectedImage(image)}
              style={{ cursor: 'pointer' }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage(image.url);
              }}
              style={{
                position: 'absolute',
                top: 'var(--spacing-xs)',
                right: 'var(--spacing-xs)',
                width: '24px',
                height: '24px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid var(--Stroke-Light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--Red-600)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.color = 'inherit';
              }}
            >
              <svg 
                style={{ width: '14px', height: '14px' }} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className={styles.imageName}>{image.name}</div>
          </div>
        ))}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className={styles.modal} 
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className={styles.modalContent} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>{selectedImage.name}</h3>
                <p className={styles.modalMeta}>
                  {formatFileSize(selectedImage.size)}
                  {selectedImage.uploadedAt && (
                    <> • Uploaded {new Date(selectedImage.uploadedAt.seconds * 1000).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              <button 
                onClick={() => setSelectedImage(null)} 
                className={styles.modalCloseButton}
              >
                <svg 
                  style={{ width: '1.5rem', height: '1.5rem' }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={styles.modalBody}>
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name} 
                className={styles.modalImage} 
              />
            </div>
            <div className={styles.modalFooter}>
              <a
                href={selectedImage.url}
                download={selectedImage.name}
                className={`${styles.actionButton} ${styles.primary}`}
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}
              >
                <svg 
                  style={{ width: '1rem', height: '1rem' }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </a>
              <button 
                onClick={() => setSelectedImage(null)} 
                className={styles.actionButton}
                style={{ 
                  backgroundColor: 'var(--Grey-200)', 
                  color: 'var(--Text-Primary)' 
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;